import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { images } from '../data/mockData';
import { fetchPractitioners, getServiceCategories, filterPractitionersByCategory } from '../lib/supabase';
import { formatPractitionerName, formatProfileText } from '../lib/profileFormatting';
import { getResponsiveImageUrl } from '../lib/imageOptimization';
import { Users } from 'lucide-react';
import LoginModal from '../components/LoginModal';

const shufflePractitioners = (practitioners) => {
  const shuffled = [...practitioners];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }

  return shuffled;
};

const PractitionersPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [practitioners, setPractitioners] = useState([]);
  const [serviceCategories, setServiceCategories] = useState(['All']);
  const [loading, setLoading] = useState(true);
  const [isTWNHubModalOpen, setIsTWNHubModalOpen] = useState(false);

  useEffect(() => {
    const loadPractitioners = async () => {
      setLoading(true);
      try {
        const data = await fetchPractitioners();
        console.log('Supabase data received:', data);
        console.log('Data length:', data?.length);
        if (data && data.length > 0) {
          console.log('First practitioner ID:', data[0].ID);
          console.log('First practitioner data:', data[0]);
        }
        setPractitioners(shufflePractitioners(data));
        const categories = getServiceCategories(data);
        console.log('Service categories:', categories);
        setServiceCategories(categories);
      } catch (error) {
        console.error('Error loading practitioners:', error);
      }
      setLoading(false);
    };

    loadPractitioners();
  }, []);

  const handleScheduleSession = () => {
    window.open('https://twnhub.online/services', '_blank', 'noopener noreferrer');
  };

  const handleTWNHubLogin = () => {
    setIsTWNHubModalOpen(true);
  };

  const filteredPractitioners = filterPractitionersByCategory(practitioners, selectedCategory);

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-yellow-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-purple-400 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <div className="w-16 h-16 mx-auto bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center mb-8">
              <Users className="w-8 h-8 text-black" />
            </div>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-8 font-title">
            Meet Our Practitioners
          </h1>

          <div className="max-w-4xl mx-auto mb-12">
            <p className="text-xl text-white leading-relaxed font-body">
              Welcome to The Wisdom Network's Practitioner Directory. Here, you can learn more about our elite team of 
              certified spiritual life coaches, herbalists, psychic readers, astrologers, healers, manifestation coaches, 
              and other holistic practitioners. Each practitioner undergoes rigorous vetting and certification by Mystic 
              University or other esteemed institutions to ensure they possess authentic skills and a deep commitment to their craft.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-16 bg-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-purple-800 mb-6 font-subtitle">
              Browse or Select Practitioners By Service Category
            </h2>
            
            <div className="max-w-md mx-auto">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full bg-white border-purple-300 text-purple-800 font-body">
                  <SelectValue placeholder="Service Categories" />
                </SelectTrigger>
                <SelectContent>
                  {serviceCategories.map((category, index) => (
                    <SelectItem key={index} value={category} className="font-body">
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Practitioners Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="text-purple-800 text-xl font-body">Loading practitioners...</div>
            </div>
          ) : filteredPractitioners.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-purple-800 text-xl font-body">No practitioners found.</div>
              <div className="text-purple-600 text-sm font-body mt-2">Total practitioners: {practitioners.length}</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filteredPractitioners.map((practitioner) => (
                <Card 
                  key={practitioner.ID}
                  className="bg-white border border-purple-200 hover:border-purple-400 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-xl group"
                >
                  <CardContent className="p-0">
                    <div className="relative">
                      <img
                        src={getResponsiveImageUrl(
                          practitioner.Photo || 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxwb3J0cmFpdCUyMHdvbWFufGVufDB8fHx8MTc1NTg5NTgwOXww&ixlib=rb-4.1.0&q=85',
                          { width: 600 },
                        )}
                        alt={formatPractitionerName(practitioner.Name)}
                        className="w-full h-64 object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                      
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-purple-900/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <Link
                          to={`/practitioners/${practitioner.slug || practitioner.ID}`}
                          className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-400 hover:to-yellow-300 text-black font-bold py-2 px-6 rounded-lg font-body"
                        >
                          Learn More
                        </Link>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-purple-800 mb-2 font-subtitle">
                        {formatPractitionerName(practitioner.Name)}
                      </h3>
                      
                      <h4 className="text-lg font-semibold text-purple-600 mb-3 font-body">
                        {formatProfileText(practitioner.Title)}
                      </h4>
                      
                      <p className="text-sm text-purple-700 leading-relaxed font-body">
                        {formatProfileText(
                          practitioner.practices ||
                            practitioner['Short Bio'] ||
                            'Spiritual Life Coach',
                        )}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Our Practitioners Section */}
      <section className="py-20 bg-purple-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-purple-800 mb-8 font-title">
              Why Choose Our Practitioners?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-12 mb-16">
            <div className="text-center">
              <h3 className="text-xl font-bold text-purple-800 mb-4 font-subtitle">Certified Excellence:</h3>
              <p className="text-purple-700 leading-relaxed font-body">
                All our practitioners are certified by Mystic University or other esteemed institutions, 
                guaranteeing authenticity and expertise.
              </p>
            </div>

            <div className="text-center">
              <h3 className="text-xl font-bold text-purple-800 mb-4 font-subtitle">Top Practitioners:</h3>
              <p className="text-purple-700 leading-relaxed font-body">
                We feature the best of the best, practitioners with real spiritual gifts and next-level skills.
              </p>
            </div>

            <div className="text-center">
              <h3 className="text-xl font-bold text-purple-800 mb-4 font-subtitle">Diverse Expertise:</h3>
              <p className="text-purple-700 leading-relaxed font-body">
                Our practitioners offer a wide range of holistic and spiritual services, 
                ensuring you find the right guidance for your needs.
              </p>
            </div>

            <div className="text-center">
              <h3 className="text-xl font-bold text-purple-800 mb-4 font-subtitle">Results-Oriented:</h3>
              <p className="text-purple-700 leading-relaxed font-body">
                Each practitioner is dedicated to delivering tangible, transformative results for you.
              </p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-lg text-purple-700 mb-8 font-body">
              Start your journey towards transformation today by connecting with one of our exceptional practitioners.
            </p>

            <Button 
              onClick={handleScheduleSession}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-8 text-lg rounded-lg transition-all duration-300 transform hover:scale-105 shadow-xl font-body"
            >
              SCHEDULE YOUR SESSION TODAY
            </Button>
          </div>
        </div>
      </section>

      {/* Bottom Branding Section */}
      <section className="py-16 bg-purple-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <img
              src={getResponsiveImageUrl(images.spiritual, { width: 600 })}
              alt="The Wisdom Network"
              className="w-32 h-20 mx-auto rounded-lg object-cover shadow-lg"
              loading="lazy"
              decoding="async"
            />
          </div>

          <h2 className="text-3xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-4 font-title">
            THE WISDOM NETWORK:
          </h2>
          
          <p className="text-xl text-yellow-200 font-semibold font-subtitle">
            VERIFIED WISDOM, POWERFUL TRANSFORMATIONS
          </p>
        </div>
      </section>

      {/* TWN Hub - Discrete Login Area */}
      <section className="py-8 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <button
            onClick={handleTWNHubLogin}
            className="text-sm text-gray-400 hover:text-gray-300 transition-colors duration-200 underline"
          >
            Admin Login
          </button>
        </div>
      </section>

      <LoginModal 
        isOpen={isTWNHubModalOpen} 
        onClose={() => setIsTWNHubModalOpen(false)} 
        userType="twn-hub" 
      />
    </div>
  );
};

export default PractitionersPage;